/**
 * Cross-tab subscription state sync.
 *
 * A user with two tabs open completes a purchase in tab 1; tab 2 still shows
 * the old tier badge / locked-doc tiles. This utility plumbs the gap.
 *
 * Three transports (in priority order):
 *  1. BroadcastChannel — primary, supported in all modern browsers.
 *  2. `localStorage` + `storage` event — fallback for older / restricted
 *     environments (private browsing in some Safari versions, etc).
 *  3. Window focus refetch (debounced) — third safety net for when both
 *     above silently fail.
 *
 * Two payload kinds:
 *  - `subscription_purchased { threadId? }` — receiver refetches
 *    `getUserMetadata` AND active-thread doc metadata.
 *  - `consultation_consumed   { threadId }` — receiver refetches
 *    `getUserMetadata` only (counter sync).
 *
 * Per design decision #13 and #14. Introduced by subscription-tiers-rewrite
 * Group 1 (tasks 1.10–1.12).
 */

const CHANNEL_NAME = "inhouse_subscription_v1";
const STORAGE_KEY = "inhouse_subscription_v1__last_event";

/**
 * Minimum interval between focus-driven `getUserMetadata` refetches. Avoids
 * hammering BE when a user is tab-switching rapidly. Exported so callers
 * (CrossTabSyncBridge) can reuse the constant instead of literal magic numbers.
 */
export const METADATA_FOCUS_REFETCH_MIN_INTERVAL_MS = 30_000;

// ─── Payload types ───────────────────────────────────────────────────────────

export interface SubscriptionPurchasedPayload {
  kind: "subscription_purchased";
  threadId?: string | null;
}

export interface ConsultationConsumedPayload {
  kind: "consultation_consumed";
  threadId: string;
}

export type CrossTabPayload =
  | SubscriptionPurchasedPayload
  | ConsultationConsumedPayload;

/**
 * Envelope written to localStorage. The timestamp lets us ignore stale or
 * self-originated events when the BroadcastChannel-fallback path is active.
 */
interface StorageEnvelope {
  payload: CrossTabPayload;
  ts: number;
  /** Random nonce so writing the same payload twice still triggers `storage`. */
  nonce: string;
}

// ─── BroadcastChannel feature detection ──────────────────────────────────────

/**
 * Checked dynamically (not at module load) so tests can stub the global in
 * `beforeEach` and have it take effect without re-importing.
 */
const getBroadcastChannelCtor = ():
  | (new (name: string) => BroadcastChannel)
  | null => {
  if (typeof globalThis === "undefined") return null;
  const ctor = (
    globalThis as {
      BroadcastChannel?: new (name: string) => BroadcastChannel;
    }
  ).BroadcastChannel;
  return typeof ctor === "function" ? ctor : null;
};

const makeChannel = (): BroadcastChannel | null => {
  const Ctor = getBroadcastChannelCtor();
  if (!Ctor) return null;
  try {
    return new Ctor(CHANNEL_NAME);
  } catch {
    return null;
  }
};

// A module-level publisher channel keeps the open count low (one per tab)
// regardless of how many components call `broadcastSubscriptionUpdated`.
let publisherChannel: BroadcastChannel | null = null;

const getPublisher = (): BroadcastChannel | null => {
  if (publisherChannel) return publisherChannel;
  publisherChannel = makeChannel();
  return publisherChannel;
};

// ─── Publish ─────────────────────────────────────────────────────────────────

/**
 * Publish a cross-tab event. Returns silently in environments where neither
 * BroadcastChannel nor localStorage are available (e.g. some sandboxes).
 */
export const broadcastSubscriptionUpdated = (payload: CrossTabPayload): void => {
  const channel = getPublisher();
  if (channel) {
    try {
      channel.postMessage(payload);
    } catch {
      // fall through to storage fallback below
    }
  }

  // Always also write to localStorage. Receivers de-dupe by nonce, so this is
  // a free belt-and-braces (and the only path for older Safari).
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    const envelope: StorageEnvelope = {
      payload,
      ts: Date.now(),
      nonce: `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));
  } catch {
    // localStorage may throw under quota / privacy mode — swallow.
  }
};

// ─── Subscribe ───────────────────────────────────────────────────────────────

export type SubscriptionCallback = (payload: CrossTabPayload) => void;

/**
 * Subscribe to subscription-update events from peer tabs. Returns an
 * unsubscribe function. Safe to call multiple times — each registration owns
 * its own channel and storage listener.
 */
export const subscribeToSubscriptionUpdates = (
  callback: SubscriptionCallback
): (() => void) => {
  const channel = makeChannel();
  const seenNonces = new Set<string>();

  const onChannelMessage = (event: MessageEvent<CrossTabPayload>) => {
    if (!event.data || typeof event.data !== "object") return;
    if (event.data.kind === "subscription_purchased" || event.data.kind === "consultation_consumed") {
      callback(event.data);
    }
  };

  const onStorage = (event: StorageEvent) => {
    if (event.key !== STORAGE_KEY || !event.newValue) return;
    let parsed: StorageEnvelope | null = null;
    try {
      parsed = JSON.parse(event.newValue) as StorageEnvelope;
    } catch {
      return;
    }
    if (!parsed || !parsed.payload || !parsed.nonce) return;
    if (seenNonces.has(parsed.nonce)) return;
    seenNonces.add(parsed.nonce);
    // Cap the de-dupe set so it doesn't grow unbounded.
    if (seenNonces.size > 64) {
      const first = seenNonces.values().next().value;
      if (first) seenNonces.delete(first);
    }
    if (
      parsed.payload.kind === "subscription_purchased" ||
      parsed.payload.kind === "consultation_consumed"
    ) {
      callback(parsed.payload);
    }
  };

  if (channel) {
    channel.addEventListener("message", onChannelMessage as EventListener);
  }
  if (typeof window !== "undefined") {
    window.addEventListener("storage", onStorage);
  }

  return () => {
    if (channel) {
      channel.removeEventListener("message", onChannelMessage as EventListener);
      try {
        channel.close();
      } catch {
        // ignore
      }
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage);
    }
  };
};

// ─── Focus-debounced refetch ─────────────────────────────────────────────────

/**
 * Subscribe to window-focus events, debounced by
 * `METADATA_FOCUS_REFETCH_MIN_INTERVAL_MS`. The callback fires at most once per
 * interval regardless of how many focus events arrive.
 *
 * The caller is responsible for the actual refetch — this utility only owns
 * the debounce + DOM glue.
 */
export const subscribeToFocusEvents = (
  callback: () => void,
  options?: { minIntervalMs?: number; now?: () => number }
): (() => void) => {
  const minInterval =
    options?.minIntervalMs ?? METADATA_FOCUS_REFETCH_MIN_INTERVAL_MS;
  const now = options?.now ?? Date.now;
  let lastFiredAt = 0;

  const onFocus = () => {
    const t = now();
    if (t - lastFiredAt < minInterval) return;
    lastFiredAt = t;
    callback();
  };

  if (typeof window === "undefined") return () => {};
  window.addEventListener("focus", onFocus);
  return () => window.removeEventListener("focus", onFocus);
};

// ─── Test seam ───────────────────────────────────────────────────────────────

/**
 * Reset module-level state. Tests call this between cases so the singleton
 * publisher channel doesn't leak across runs.
 */
export const __resetForTests = (): void => {
  if (publisherChannel) {
    try {
      publisherChannel.close();
    } catch {
      // ignore
    }
  }
  publisherChannel = null;
};
